import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Modal,
  Select,
  Button,
  Input,
  message,
  Col,
  Row,
  Divider,
  Spin,
} from "antd";
import dayjs from "dayjs";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";
import ViewDocument from "reusableComponent/Popup/viewDocument";
import download from "downloadjs";

const { Option } = Select;
const { TextArea } = Input;

/* ─── Styled ─────────────────────────────────────────────── */

const ModalWrap = styled(Modal)`
  .ant-modal-content {
    border-radius: 14px;
    padding: 10px 8px;
  }

  .ant-modal-header {
    border-bottom: none;
    padding-bottom: 0;
  }

  .ant-modal-title {
    font-size: 20px;
    font-weight: 600;
    margin-top: -12px;
  }

  .ant-modal-body {
    padding: 10px 20px 24px;
  }

  /* top right icons */
  .top-icons {
    position: absolute;
    right: 55px;
    top: 18px;
    display: flex;
    gap: 18px;
    font-size: 18px;
    color: #5b6fd6;
    cursor: pointer;
  }

  .close-icon {
    position: absolute;
    right: 20px;
    top: 18px;
    font-size: 18px;
    cursor: pointer;
    color: #666;
  }

  .applied-tag {
    background: #e9ecfb;
    border-radius: 20px;
    padding: 3px 14px;
    font-size: 10px;
    margin-bottom: 18px;
    display: inline-block;
  }
  .applied-tag-red {
    background: #ffebee;
    border-radius: 20px;
    padding: 3px 14px;
    font-size: 10px;
    margin-bottom: 18px;
    display: inline-block;
    color: red;
  }
  .applied-tag-green {
    background: #e8f5e9;
    border-radius: 20px;
    padding: 3px 14px;
    font-size: 10px;
    margin-bottom: 18px;
    display: inline-block;
    color: green;
  }

  .label {
    color: #8b8b8b;
    font-weight: 500;
  }

  .value {
    color: #1f1f1f;
    font-weight: 500;
    // text-align: right;
  }

  .attachment {
    color: #2d53da;
    cursor: pointer;
  }

  .reason-title {
    margin-top: 16px;
    color: #8b8b8b;
    font-weight: 500;
  }

  .reason-text {
    margin-top: 6px;
    color: #1f1f1f;
    line-height: 1.6;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .value {
      text-align: left;
      margin-top: 4px;
    }

    .top-icons {
      right: 45px;
      gap: 12px;
    }

    .ant-modal-body {
      padding: 10px 14px 22px;
    }
  }
`;
const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  margin: 16px 0 8px;
`;

/* ─── Status transitions ─────────────────────────────────── */
const STATUS_OPTIONS = {
  Open: ["In Progress", "Resolved", "Rejected"],
  "In Progress": ["Resolved", "Rejected"],
};

/* ─── Component ──────────────────────────────────────────── */
const TicketUpdateModal = ({ open, onClose, ticket, onSuccess }) => {
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const { t } = useTranslation();
  const [newStatus, setNewStatus] = useState(null);
  const [rejectedReason, setRejectedReason] = useState("");
  const [resolvedNotes, setResolvedNotes] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewURL, setPreviewURL] = useState();

  const handlePreviewClick = async (data) => {
    try {
      const preview = data?.ticket_filename?.split(".")?.pop();
      setLoading(true);

      const { code, url } = await CALL_API(
        `assets/ticket-file/${data?.ticketid}`,
        "get",
        {}
      );

      if (code === STATUS_CODE.SUCCESS) {
        if (["jpg", "jpeg", "png", "svg", "pdf"].includes(preview)) {
          setPreviewURL(url);
          setPreview(true);
        } else {
          download(url);
          setPreview(false);
        }
      } else {
        message.error("Preview failed. Try again.");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setNewStatus(null);
      setRejectedReason("");
      setResolvedNotes("");
    }
  }, [open]);

  const isUpdatable =
    ticket?.ticket_status === "Open" || ticket?.ticket_status === "In Progress";
  const statusOptions = STATUS_OPTIONS[ticket?.ticket_status] ?? [];

  const handleUpdate = async () => {
    if (!newStatus) {
      message.warning("Please select a status to update");
      return;
    }
    if (newStatus === "Rejected" && !rejectedReason.trim()) {
      message.warning("Please enter a reason for rejection");
      return;
    }
    if (newStatus === "Resolved" && !resolvedNotes.trim()) {
      message.warning("Please enter resolution notes");
      return;
    }

    try {
      setUpdateLoading(true);

      const now = new Date();
      // TODO: replace with actual logged-in user id
      const actorId = userInfo?.userid;

      let payload = {};

      if (newStatus === "Rejected") {
        payload = {
          ticket_status: "Rejected",
          rejected_reason: rejectedReason.trim(),
          rejected_by: actorId,
          rejected_on: now,
          resolved_notes: "",
          resolved_by: "",
          resolved_on: new Date(),
        };
      } else if (newStatus === "Resolved") {
        payload = {
          ticket_status: "Resolved",
          rejected_reason: "",
          rejected_by: "",
          rejected_on: "",
          resolved_notes: resolvedNotes.trim(),
          resolved_by: actorId,
          resolved_on: now,
        };
      } else {
        payload = {
          ticket_status: newStatus,
          rejected_reason: "",
          rejected_by: "",
          rejected_on: "",
          resolved_notes: "",
          resolved_by: "",
          resolved_on: "",
        };
      }

      const { code } = await CALL_API(
        `/assets/update-ticket-status/${ticket?.ticketid}`,
        "patch",
        payload
      );

      if (code === STATUS_CODE.SUCCESS) {
        message.success("Ticket status updated successfully");
        onSuccess?.();
        onClose();
      } else {
        throw new Error(`Failed: ${code}`);
      }
    } catch (error) {
      message.error("Failed to update ticket status");
      console.error(error);
    } finally {
      setUpdateLoading(false);
    }
  };

  /* ── Footer: read-only statuses have no footer ── */
  const footer = isUpdatable
    ? [
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancel
        </Button>,
        <Button
          key="update"
          type="primary"
          loading={updateLoading}
          onClick={handleUpdate}
          style={{ background: "#2563eb", borderRadius: 6 }}
        >
          Update
        </Button>,
      ]
    : null;

  return (
    <>
      {!preview && (
        <ModalWrap
          title="Asset Ticket"
          open={open}
          onCancel={onClose}
          centered
          width={500}
          footer={footer}
        >
          {/* ── Badges ── */}
          <div className="applied-tag me-2">
            {t("Applied on")}&nbsp;&nbsp;
            <b className="value">
              {dayjs(ticket?.createdAt).format("Do MMM, YYYY")} by{" "}
              {
                userList?.find((item) => item?.userid === ticket?.userid)
                  ?.fullname
              }
            </b>
          </div>
          {ticket?.ticket_status === "Rejected" && (
            <div className="applied-tag-red">
              {t("Rejected by")}&nbsp;&nbsp;
              <b className="value">
                {
                  userList?.find((item) => item?.userid === ticket?.rejected_by)
                    ?.fullname
                }
                ; {dayjs(ticket?.rejected_on).format("Do MMM, YYYY")}
              </b>
            </div>
          )}
          {ticket?.ticket_status === "Resolved" && (
            <div className="applied-tag-green">
              {t("Resolved by")}&nbsp;&nbsp;
              <b className="value">
                {
                  userList?.find((item) => item?.userid === ticket?.resolved_by)
                    ?.fullname
                }
                ; {dayjs(ticket?.resolved_on).format("Do MMM, YYYY")}
              </b>
            </div>
          )}

          {/* ── Read-only fields ── */}
          <Row gutter={[8, 14]}>
            <Col xs={24} sm={12}>
              <div className="label">{t("Asset")}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="value">{ticket?.assetname}</div>
            </Col>

            {/* end date */}
            <Col xs={24} sm={12}>
              <div className="label">{t("Title")}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="value">{ticket?.title}</div>
            </Col>

            {/* total days */}
            <Col xs={24} sm={12}>
              <div className="label">{t("Priority")}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="value">{ticket?.priority}</div>
            </Col>

            {/* attachment */}
            <Col xs={24} sm={12}>
              <div className="label">{t("Attachment")}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div
                className="value attachment"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  // setOpen(false);
                  handlePreviewClick(ticket);
                }}
              >
                {ticket?.ticket_filename ? ticket?.ticket_filename : "-"}
              </div>
            </Col>
          </Row>

          <div className="reason-title">{t("Description")}</div>
          <div className="reason-text">{ticket?.ticket_description}</div>
          {ticket?.ticket_status === "Resolved" && (
            <>
              <div className="reason-title">{t("Notes")}</div>
              <div className="reason-text">{ticket?.resolved_notes}</div>
            </>
          )}
          {ticket?.ticket_status === "Rejected" && (
            <>
              <div className="reason-title">{t("Reason for Rejection")}</div>
              <div className="reason-text">{ticket?.rejected_reason}</div>
            </>
          )}

          {/* ── Updatable: status dropdown + conditional textarea ── */}
          {isUpdatable && (
            <>
              <SectionLabel>Update Status</SectionLabel>
              <Select
                placeholder="Select new status"
                style={{ width: "100%" }}
                value={newStatus}
                onChange={(val) => {
                  setNewStatus(val);
                  setRejectedReason("");
                  setResolvedNotes("");
                }}
              >
                {statusOptions.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>

              {newStatus === "Rejected" && (
                <>
                  <SectionLabel>Reason for Rejection</SectionLabel>
                  <TextArea
                    rows={3}
                    placeholder="Enter reason for rejection..."
                    value={rejectedReason}
                    onChange={(e) => setRejectedReason(e.target.value)}
                    style={{ borderRadius: 6 }}
                  />
                </>
              )}

              {newStatus === "Resolved" && (
                <>
                  <SectionLabel>Resolution Notes</SectionLabel>
                  <TextArea
                    rows={3}
                    placeholder="Enter resolution notes..."
                    value={resolvedNotes}
                    onChange={(e) => setResolvedNotes(e.target.value)}
                    style={{ borderRadius: 6 }}
                  />
                </>
              )}
            </>
          )}
        </ModalWrap>
      )}
      <Spin spinning={loading}>
        <ViewDocument
          open={preview}
          onCancel={() => {
            setPreview(false);
            setPreviewURL("");
          }}
          data={previewURL}
        />
      </Spin>
    </>
  );
};

export default TicketUpdateModal;
